import {StandardType} from "../../../../utils/testsStandards/_standardType";
import * as React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CheckBoxField from "../../../../shared/form/CheckBoxField";
import {Accordion, AccordionDetails, AccordionSummary} from "../../../../shared/ui/CustomAccordion";
import {MAX_SELECTED_STANDARDS, selectedStandardsCount} from "../utils/functions";
import IconTooltip from "../../../../shared/ui/StyledTooltip";
import {alpha, Chip, Stack} from "@mui/material";
import {styled} from "@mui/material/styles";
import Icon from "../../../../shared/ui/Icon";
import {useMemo} from "react";

interface IStandardCategoriesListing {
    categories: StandardType[];
    isWorkPlan?: boolean;
    setSelectedStandardsHandler: (event: React.ChangeEvent<HTMLInputElement>, categoryIndex: number, subIndex: number, competenceIndex: number) => void,
}

const CompetenceCard = styled(Box)(({theme}) => ({
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
}));

const getCategorySelectedCount = (category: StandardType): number => {
    return (category.sub ?? []).reduce(
        (subTotal: number, sub: any) =>
            subTotal +
            (sub.competenta ?? []).reduce(
                (compTotal: number, comp: any) =>
                    compTotal + (comp.standarde ?? []).filter((s: any) => s.checked).length,
                0
            ),
        0
    );
};

const CompetenceItem = React.memo(
    ({
         competenta,
         categoryIndex,
         subIndex,
         competenceIndex,
         isMaxReached,
         onStandardChange,
    }: any) => {
        const options = useMemo(
            () =>
                competenta.standarde?.map((item: any) => ({
                    ...item,
                    label: item.text,
                    value: item.id,
                    checked: !!item.checked,
                    disabled: isMaxReached && !item.checked,
                })),
            [competenta.standarde, isMaxReached]
        );

        return (
            <CompetenceCard>
                <Stack direction="row" spacing={1} alignItems="flex-start" mb={1}>
                    <Icon
                        icon="fas circle-check"
                        style={{
                            fontSize: "14px",
                            marginTop: "4px",
                            color: "#0064FF",
                        }}
                    />
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color="text.primary"
                        lineHeight={1.4}
                    >
                        {competenta.text}
                    </Typography>
                </Stack>

                <CheckBoxField
                    name="chapters"
                    type="checkbox-group"
                    label="Alege capitolele"
                    labelPlacement="end"
                    options={options}
                    onChange={(event) =>
                        onStandardChange(event, categoryIndex, subIndex, competenceIndex)
                    }
                />
            </CompetenceCard>
        );
    }
);

const StandardCategoriesListing = ({categories, setSelectedStandardsHandler, isWorkPlan = false}: IStandardCategoriesListing) => {
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [expandedSub, setExpandedSub] = React.useState<string | false>(false);
    const totalSelectedCount = useMemo(
        () => selectedStandardsCount(categories),
        [categories]
    );
    const isMaxReached = isWorkPlan ? totalSelectedCount >= 1 : totalSelectedCount >= MAX_SELECTED_STANDARDS;

    const handleChange =
        (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    const handleChangeSub =
        (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpandedSub(newExpanded ? panel : false);
        };

    return (
        <>
            {categories.map((category, categoryIndex) => (
                <Accordion
                    sx={(theme) => ({marginBottom: theme.spacing(1),})}
                    expanded={expanded === `panel${categoryIndex}`}
                    onChange={handleChange(`panel${categoryIndex}`)}
                    key={`panel${categoryIndex}`}
                >
                    <AccordionSummary
                        aria-controls={`panel${categoryIndex}d-content`}
                        id={`panel${categoryIndex}d-header`}
                        sx={{
                            "& .MuiAccordionSummary-content": {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                margin: 1,
                            }
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                                variant="h4"
                                sx={{
                                    padding: 0.8,
                                    color: "primary.main",
                                    fontWeight: 500,
                                    fontSize: {xs: "1.05rem", sm: "1.2rem"},
                                }}
                            >
                                {category.title}
                            </Typography>
                            {category.note && <IconTooltip title={category.note}/>}
                        </Stack>

                        {getCategorySelectedCount(category) > 0 && (
                            <Chip
                                label={`${getCategorySelectedCount(category)} selectate`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{fontSize: "0.75rem", height: 22}}
                            />
                        )}
                    </AccordionSummary>

                    <AccordionDetails sx={{pt: 1, pb: 1}}>
                        {category.sub.map((sub: any, subIndex: number) => (
                            sub.title ? (
                                <Accordion
                                    expanded={expandedSub === `panel${subIndex}`}
                                    onChange={handleChangeSub(`panel${subIndex}`)}
                                    key={`panel${subIndex}`}
                                    sx={(theme) => ({
                                        backgroundColor: alpha(theme.palette.primary.light, 0.12),
                                        marginBottom: theme.spacing(1),
                                    })}
                                >
                                    <AccordionSummary
                                        aria-controls={`panel${categoryIndex}d-content`}
                                        id={`panel${categoryIndex}d-header`}
                                        sx={{
                                            "& .MuiAccordionSummary-content": {
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: "100%",
                                                margin: 1,
                                            }
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography
                                                variant="subtitle1"
                                                color="text.primary"
                                                fontWeight={500}
                                                sx={{fontSize: "1rem"}}
                                            >
                                                {sub.title}
                                            </Typography>
                                            {sub.note && <IconTooltip title={sub.note}/>}
                                        </Stack>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{pt: 1, pb: 1}}>
                                        {sub.competenta.map((competenta: any, competenceIndex: number) => (
                                            <CompetenceItem
                                                key={competenta.id ?? competenceIndex}
                                                competenta={competenta}
                                                categoryIndex={categoryIndex}
                                                subIndex={subIndex}
                                                competenceIndex={competenceIndex}
                                                isMaxReached={isMaxReached}
                                                onStandardChange={setSelectedStandardsHandler}
                                            />
                                        ))}
                                    </AccordionDetails>

                                </Accordion>
                            ) : (
                                sub.competenta.map((competenta: any, competenceIndex: number) => (
                                    <Box ml={2}>
                                        <CompetenceItem
                                            key={competenta.id ?? competenceIndex}
                                            competenta={competenta}
                                            categoryIndex={categoryIndex}
                                            subIndex={subIndex}
                                            competenceIndex={competenceIndex}
                                            isMaxReached={isMaxReached}
                                            onStandardChange={setSelectedStandardsHandler}
                                        />
                                    </Box>
                                ))
                            )

                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}
        </>

    )

}

export default StandardCategoriesListing;